/* eslint-disable */
//// @ts-nocheck
import { IComponentOptions, IPromise, ITimeoutService } from "angular";
import { StateService } from "@uirouter/angular";
import { stratify } from "d3-hierarchy";
import { HierarchyNode, TreeLayout, hierarchy, tree } from "d3-hierarchy";
import { Selection, select } from "d3-selection";
import { D3ZoomEvent, ZoomBehavior, zoom, zoomIdentity } from "d3-zoom";
import { AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { GtmhubController } from "@gtmhub/core";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { localize } from "@gtmhub/localization";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { ncIconOutlineMap } from "@gtmhub/shared/tree-renderer/nc-outline-icons";
import { ISocketV2Data } from "@gtmhub/sockets";
import { INgRedux } from "@gtmhub/state-management";
import { ITeam, ITeamsStoreState, TeamsActions, addAssigneeDataToTeams } from "@gtmhub/teams";
import { TeamService } from "@gtmhub/teams/team.service";
import { Assignee } from "@webapp/assignees/models/assignee.models";
import { NAV_WIDTH } from "@webapp/navigation/components/navigation/config";
import { confirmDelete } from "@webapp/shared/modal/confirm";
import { UiModalService } from "@webapp/ui/modal/services/modal.service";

const bindStateToCtrl = (state: ITeamsStoreState & IAssigneesStoreState) => {
  const { items: teamsItems, isFetched: teamsFetched, error: teamsError } = state.teams;
  const { map: assigneeIdMap, isFetched: assigneesFetched, error: assigneesError } = state.assignees;

  return {
    teamsItems: addAssigneeDataToTeams(teamsItems, assigneeIdMap),
    assigneeIdMap,
    reduxItemsFetched: teamsFetched && assigneesFetched,
    reduxItemsError: teamsError || assigneesError,
    noTeamsInAccount: state.teams.isFetched && state.teams.items.length === 0,
  };
};

const margin = { top: 0, right: 10, bottom: 0, left: 10 };
const width = window.innerWidth - margin.right - margin.left;
const height = window.innerHeight - margin.top - margin.bottom;

interface Node {
  membersCount: number;
  id: string;
  parentId: string;
}

interface OrgChartV3ComponentCtrl extends ReturnType<typeof bindStateToCtrl> {}
class OrgChartV3ComponentCtrl extends GtmhubController {
  public indicators: {
    loading: boolean;
  };

  private zoom?: ZoomBehavior<Element, unknown>;

  private contextMenuItem: Assignee;
  private rootHierarchyNode: HierarchyNode<HierarchyNode<Node>>;
  private svg: Selection<SVGSVGElement, unknown, HTMLElement, any>;
  private tree: TreeLayout<HierarchyNode<Node>>;
  private mainGroup;
  private zoomRatio = 1;

  public showSomeTeamMembers: boolean;

  static $inject = [
    "$scope",
    "$ngRedux",
    "TeamsActions",
    "AssigneeActions",
    "UIErrorHandlingService",
    "$rootScope",
    "$timeout",
    "Ng1UiModalService",
    "TeamService",
    "$state",
  ];

  constructor(
    private $scope: IExtendedRootScopeService,
    $ngRedux: INgRedux,
    teamsActions: TeamsActions,
    assigneeActions: AssigneeActions,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $rootScope,
    private $timeout: ITimeoutService,
    private modalService: UiModalService,
    private teamService: TeamService,
    private $state: StateService
  ) {
    super();

    this.onDestroy($ngRedux.connect(bindStateToCtrl)(this));
    $ngRedux.dispatch(teamsActions.fetchTeamsIfMissing());
    $ngRedux.dispatch(assigneeActions.getAssignees());
  }

  public expand = (d) => {
    const children = d.children ? d.children : d._children;
    if (d._children) {
      d.children = d._children;
      d._children = null;
    }

    if (children) {
      children.forEach(this.expand);
    }
  };

  public expandAll = (): void => {
    this.rootHierarchyNode.children.forEach(this.expand);
    this.rootHierarchyNode.descendants().forEach((d: any, i) => {
      d.id = i;
      d._children = d.children;

      if (d.depth > 6) {
        d.children = null;
      }
    });
    this.updateSvg();
  };

  public collapse = (d) => {
    if (d.children) {
      d._children = d.children;
      d._children.forEach(this.collapse);
      d.children = null;
    }
  };

  public collapseAll = (): void => {
    this.rootHierarchyNode.children.forEach(this.collapse);
    this.updateSvg();
  };

  private constructTree = (data: any[]) => {
    const nodes = JSON.parse(JSON.stringify(data));

    for (let i = 0; i < nodes.length; i++) {
      if (nodes[i].parentId === "") {
        nodes[i].parentId = "root";
      }

      if (!nodes[i].members) {
        nodes[i].members = [];
      }

      nodes[i].membersCount = nodes[i].members?.length || 0;
    }

    const root = {
      id: "root",
      type: "root",
    };

    nodes.push(root);

    return stratify<Node>()
      .id((d) => d.id)
      .parentId((d) => d.parentId)(nodes);
  };

  private createTreeLayout = () => {
    return tree<HierarchyNode<Node>>()
      .nodeSize([30, 750]) // dynamic tree size
      .separation((a, b) => {
        let spaceRequired = 4;

        if (b.parent.id === a.parent.id) {
          spaceRequired = spaceRequired + b.data.data.membersCount;
        }

        if (b.parent.id !== a.parent.id) {
          spaceRequired = spaceRequired + a.data.data.membersCount;
        }

        return spaceRequired;
      });
  };

  private isSvgInit = (): boolean => {
    return !!this.svg;
  };

  private initZoom() {
    return zoom()
      .scaleExtent([0.1, 3])
      .on("zoom", (event: D3ZoomEvent<Element, unknown>) => {
        this.zoomRatio = event.transform.k;
        this.mainGroup.attr("transform", event.transform.toString());
        this.contextMenuItem && this.closeContextMenu();
      });
  }

  private translateTreeToFirstNode(): void {
    const allNodes = this.mainGroup
      .selectAll("g.nodeholder")
      .filter((d) => d.data.depth === 1)
      .data();

    /** root nodes are reversed, that's why 'lastNode' is actually the first appearing most at topleft position  */
    const lastNode = allNodes[allNodes.length - 1];

    if (lastNode) {
      const left = -lastNode.y * this.zoomRatio + 75;
      const top = -lastNode.x * this.zoomRatio + 75;
      this.translateTreeToCoordinates(left, top);
    }
  }

  private translateTreeToCoordinates = (left: number, top: number) => {
    return this.mainGroup
      .attr("transform", `translate(${left}, ${top}) scale(${this.zoomRatio})`)
      .transition()
      .duration(200)
      .end()
      .finally(() => {
        if (this.zoom) {
          const transform = zoomIdentity.translate(left, top).scale(this.zoomRatio);
          this.svg.call(this.zoom.transform, transform).call(this.zoom);
        }
      });
  };

  private initSvg = () => {
    this.svg = select("#org-tree-container").append("svg").attr("width", width).attr("height", height);

    if (!this.zoom) {
      this.zoom = this.initZoom();
      this.svg.call(this.zoom);
    }

    const AVATAR_WIDTH = 40;
    const avatarRadius = AVATAR_WIDTH / 2;
    const defs = this.svg.append("defs");

    defs.append("clipPath").attr("id", "avatar-clip").append("circle").attr("cx", avatarRadius).attr("cy", avatarRadius).attr("r", avatarRadius);
    defs.append("clipPath").attr("id", "avatar-clip2").append("circle").attr("cx", 8).attr("cy", 8).attr("r", 8);
    defs.append("clipPath").attr("id", "avatar-clip3").append("circle").attr("cx", 15).attr("cy", 15).attr("r", 15);

    this.mainGroup = this.svg.append("g").attr("class", "main");
  };

  private render = (data: { items: any[]; translateToFirstNode: boolean }) => {
    if (!this.isSvgInit()) {
      this.initSvg();
    }

    const treeData = this.constructTree(data.items);
    this.tree = this.createTreeLayout();
    this.rootHierarchyNode = hierarchy(treeData);

    const rootDesc = this.rootHierarchyNode.descendants();
    rootDesc.forEach((d: any, i) => {
      d.id = i;
      d._children = d.children;

      if (d.depth > 6) {
        d.children = null;
      }
    });

    this.updateSvg();

    /** on initial render move the tree to the first node, if tree updates, the svg position should remain untouched */
    if (data.translateToFirstNode) {
      this.translateTreeToFirstNode();
    }
  };

  private configureTeamMembers = (node): void => {
    const membersContainer = node
      .select(".members")
      .selectAll(".member")
      .data(
        (d) => {
          const members = d.data.data.members.map((id) => {
            return {
              ...this.assigneeIdMap[id],
              id,
              x: d.x,
              y: d.y,
            };
          });

          return members;
        },
        (d, i) => {
          return d.id;
        }
      )
      .join((enter) => {
        const memberEnter = enter
          .append("g")
          .attr("class", "member")
          .attr("transform", (d, it) => {
            return "translate(0, " + it * 30 + ")";
          })
          .on("click", (evt: MouseEvent, d) => {
            this.$state.go(".employee", { employeeId: d.id });
          });

        memberEnter
          .append("text")
          .classed("dropdown-toggler", true)
          .text("...")
          .on("click", (evt: MouseEvent, d) => {
            evt.stopPropagation();
            evt.preventDefault();

            this.openContextMenu(evt, { ...d, type: "user" });
          });

        memberEnter
          .append("text")
          .attr("class", "mn")
          .text((d) => this.wrapText(d.name));

        memberEnter
          .append("image")
          .attr("xlink:href", (m) => {
            return m.picture || "";
          })
          .attr("class", "assignee-picture")
          .attr("width", 16)
          .attr("height", 16)
          .attr("clip-path", "url(#avatar-clip2)");
      })
      .attr("transform", (d, it) => {
        return "translate(0, " + it * 30 + ")";
      });

    membersContainer.exit().remove();
  };

  private configureNodes = (nodes) => {
    const node = this.mainGroup.selectAll("g.nodeholder").data(nodes, (d) => d.data.data.id);

    // Enter any new nodes at the parent's previous position.
    const nodeEnter = node
      .enter()
      .append("g")
      .attr("class", (d) => {
        if (d.depth === 0) {
          return "nodeholder root";
        }
        return "nodeholder";
      })
      .filter((d) => d.data.id !== "root")
      .attr("transform", (d) => `translate(${d.y},${d.x})`);

    nodeEnter
      .append("text")
      .attr("id", "nodeSymbol")
      .attr("dx", (d) => {
        return !d.depth || !d._children ? "" : d.children ? -0.17 + "em" : -0.3 + "em";
      })
      .attr("dy", (d) => {
        return !d.depth || !d._children ? "" : d.children ? 0.25 + "em" : 0.35 + "em";
      })
      .style("fill", "#")
      .text((d) => {
        // no text for root and terminal nodes
        // if children are rendered, show - else show +
        return !d.depth || !d._children ? "" : d.children ? "-" : "+";
      })
      .style("font-size", "30px")
      .on("click", (event, d) => {
        d.children = d.children ? null : d._children;
        this.updateSvg();
      });

    nodeEnter
      .append("rect")
      .attr("class", "pad")
      .attr("width", 300)
      .attr("height", (d) => {
        let height = d.data.data.membersCount * 30;
        height = height + 105;
        return height || 0;
      });

    nodeEnter
      .append("g")
      .attr("class", "ta")
      .filter((d) => {
        if (d.data.data.picture != "") {
          return true;
        }
        return false;
      })
      .append("image")
      .attr("xlink:href", (d) => {
        return d.data.data.picture || "";
      })
      .attr("class", "tp")
      .attr("clip-path", "url(#avatar-clip)");

    nodeEnter
      .append("g")
      .attr("class", "tma")
      .append("image")
      .attr("xlink:href", (d) => {
        const manager = this.assigneeIdMap[d.data.data.manager];
        return manager?.picture || "";
      })
      .attr("class", "tma-image")
      .attr("clip-path", "url(#avatar-clip3)");

    nodeEnter
      .append("text")
      .attr("class", "tmn")
      .text((d) => {
        const manager = this.assigneeIdMap[d.data.data.manager];
        return this.wrapText(manager?.name);
      })
      .on("click", (evt: MouseEvent, d) => {
        const manager = this.assigneeIdMap[d.data.data.manager];
        this.$state.go(".employee", { employeeId: manager.id });
      });

    nodeEnter.append("text").attr("class", "tmnl").text("Team manager");

    nodeEnter
      .filter((d) => {
        if (d.data.data.picture === "") {
          return true;
        }
        return false;
      })
      .append("circle")
      .attr("class", "ti")
      .attr("r", 20)
      .attr("fill", (d) => {
        return d.data.data.color;
      });

    nodeEnter
      .filter((d) => {
        if (d.data.data.picture === "") {
          return true;
        }
        return false;
      })
      .append("text")
      .text((d) => {
        return ncIconOutlineMap[d.data.data.avatar];
      })
      .attr("class", "tai nc-icon-outline");

    nodeEnter
      .append("text")
      .attr("class", "tn")
      .attr("dy", 2.3) // align text next to nodes
      .attr("dx", 2.4)
      .filter((d) => d.data.id !== "root")
      .text((d) => this.wrapText(d.data.data.name))
      .on("click", (evt: MouseEvent, d) => {
        this.$state.go(".team", { teamId: d.data.data.id });
      });

    nodeEnter
      .append("text")
      .classed("dropdown-toggler", true)
      .text("...")
      .on("click", (evt: MouseEvent, d) => {
        evt.stopPropagation();
        evt.preventDefault();

        this.openContextMenu(evt, { ...d.data.data, type: "team" });
      });

    nodeEnter.append("g").attr("class", "members");
    this.configureTeamMembers(nodeEnter);

    // Transition nodes to their new position.
    const nodeUpdate = node
      .merge(nodeEnter)
      .filter((d) => d.data.id !== "root")
      .attr("transform", (d) => `translate(${d.y},${d.x})`);

    nodeUpdate.select(".pad").attr("height", (d) => {
      let height = d.data.data.membersCount * 30;
      height = height + 105;

      return height || 0;
    });

    nodeUpdate.select(".tn").text((d) => this.wrapText(d.data.data.name));

    nodeUpdate.select(".tmn").text((d) => {
      const manager = this.assigneeIdMap[d.data.data.manager];
      return this.wrapText(manager?.name);
    });

    nodeUpdate.select(".tma-image").attr("xlink:href", (d) => {
      const manager = this.assigneeIdMap[d.data.data.manager];
      return manager?.picture || "";
    });

    nodeUpdate
      .select("#nodeSymbol")
      .attr("x", 320)
      .attr("y", 20)
      .text((d) => {
        // no text for root and terminal nodes
        // if children are rendered, show - else show +
        return !d.depth || !d._children ? "" : d.children ? "-" : "+";
      })
      .style("font-size", "40px");

    this.configureTeamMembers(nodeUpdate);

    // Transition exiting nodes to the parent's new position.
    node.exit().remove();
  };

  private openContextMenu = (evt: MouseEvent, data): void => {
    this.contextMenuItem = data;

    this.$timeout(() => {
      const contextMenuEl = document.getElementById("org-context-menu");
      const dropDownWidth = parseInt(contextMenuEl.style.minWidth, 10);
      const distanceToRightScreenEnd = window.innerWidth - evt.pageX - dropDownWidth;
      const offsetLeft = distanceToRightScreenEnd < 0 ? dropDownWidth : 0;

      const left = evt.pageX - this.getLeftNavWidth() - 30;
      let top = evt.pageY;
      contextMenuEl.style.left = `${left - offsetLeft}px`;
      contextMenuEl.style.top = `${top}px`;
      contextMenuEl.style.display = `block`;
    });
  };

  private configureLinks = (links): void => {
    const link = this.mainGroup.selectAll("path").data(links);
    const linkEnter = link
      .enter()
      .insert("path", "g")
      .attr("class", (d) => {
        if (d.source.depth === 0) {
          return "link root";
        }
        return "link";
      });

    // Transition links to their new position.
    link
      .merge(linkEnter)
      .attr("class", (d) => {
        if (d.source.depth === 0) {
          return "link root";
        }
        return "link";
      })
      .attr("d", (d) => {
        return (
          "M" +
          (d.source.y + 340) +
          "," +
          (d.source.x + 20) +
          " " +
          ((d.source.y + d.target.y) / 2 + 120) +
          "," +
          (d.source.x + 20) +
          " " +
          ((d.source.y + d.target.y) / 2 + 120) +
          "," +
          (d.target.x + 20) +
          " " +
          (d.target.y - 30) +
          "," +
          (d.target.x + 20)
        );
      });

    link.exit().remove();
  };

  private updateSvg = () => {
    const nodes = this.rootHierarchyNode.descendants().reverse();
    const links = this.rootHierarchyNode.links();

    this.tree(this.rootHierarchyNode);

    this.configureNodes(nodes);
    this.configureLinks(links);
  };

  public removeFromTeam = (contextMenuItem: Assignee): void => {
    const team = this.teamsItems.find((team) => team.members?.includes(contextMenuItem.id));

    this.teamService.removeTeamMember(team.id, contextMenuItem.id).then(
      () => null,
      (error) => {
        this.uiErrorHandlingService.handleModal(error);
      }
    );
  };

  public deleteTeam = (contextMenuItem: Assignee): void => {
    confirmDelete(this.modalService, {
      title: localize("delete_team_popup_title"),
      description: localize("delete_team_popup_description"),
    }).subscribe(() => this.handleDeleteTeam(contextMenuItem));
  };

  private handleDeleteTeam(team: ITeam): IPromise<void> {
    return this.teamService.deleteTeam(team.id).then(
      () => null,
      (error) => {
        this.uiErrorHandlingService.handleModal(error);
      }
    );
  }

  private getLeftNavWidth = (): number => {
    if (this.$rootScope.hideMenuV2) {
      return NAV_WIDTH.collapsed;
    }
    return NAV_WIDTH.expanded;
  };

  private closeContextMenu = (): void => {
    this.$timeout(() => {
      delete this.contextMenuItem;
    });
  };

  private onTeamDeleted = (team: ITeam): void => {
    const foundParentTeam = this.teamsItems.find((t) => t.id === team.parentId);
    this.teamsItems
      .filter((k) => k.parentId === team.id)
      .forEach((t) => {
        if (foundParentTeam) {
          t.parentId = foundParentTeam.id;
        }
        if (!foundParentTeam) {
          t.parentId = "";
        }
      });
  };

  private onTeamUpdated = (updatedTeam: ITeam): void => {
    this.teamsItems = this.teamsItems.map((t) => {
      if (t.id === updatedTeam.id) {
        return { ...t, ...updatedTeam };
      }

      return t;
    });
  };

  private wrapText = (name: string, maxChars = 24): string => {
    if (!name) {
      return "";
    }

    if (name.length > 24) {
      return name.slice(0, maxChars) + "...";
    }

    return name;
  };

  $onDestroy(): void {
    document.body.removeEventListener("click", this.closeContextMenu);
  }

  $onInit(): void {
    document.body.addEventListener("click", this.closeContextMenu);

    this.$rootScope.$on("accountTeamsUpdatedV2", (_, args: ISocketV2Data<ITeam>) => {
      if (args.modified) {
        const updatedTeam = args.modified.items[0];
        this.onTeamUpdated(updatedTeam);
        this.render({ items: this.teamsItems, translateToFirstNode: false });
        return;
      }

      if (args.removed) {
        /** when a team is deleted we need to shift child-teams to the deleted team's parent */
        const team = args.removed.items[0] as ITeam;
        this.onTeamDeleted(team);
      }

      this.render({ items: this.teamsItems, translateToFirstNode: false });
    });

    this.showSomeTeamMembers = true;
    this.indicators = {
      loading: true,
    };

    this.$scope
      .watchUntilAndRejectOnError(
        () => this.reduxItemsFetched,
        () => this.reduxItemsError
      )
      .then(() => {
        this.render({ items: this.teamsItems, translateToFirstNode: true });
        this.collapseAll();

        delete this.indicators.loading;
      })
      .catch((error) => {
        this.indicators.loading = false;
        this.uiErrorHandlingService.handleModal(error);
      });
  }
}

export const OrgChartV3Component: IComponentOptions = {
  controller: OrgChartV3ComponentCtrl,
  bindings: {},
  template: require("./org-chart-v3.component.html"),
};
